import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageSEO } from "@/components/PageSEO";
import PricingGrid from "@/pages/pricing/PricingGrid";
import PricingFaq from "@/pages/pricing/PricingFaq";
import AddOnsSection from "@/pages/pricing/AddOnsSection";
import { pricingTiers, pricingFaqs, addOnGroups } from "@/pages/pricing/pricingData";

export default function Pricing() {
  return (
    <div data-testid="pricing-page" className="pt-16 lg:pt-20">
      <PageSEO
        title="Pricing — No Website Building Fees | From £90/month"
        description="Simple monthly website plans from £90/month. Hosting, domain, and support included. No build fees — choose Launch, Grow, Scale, or Dominate. Based in Lowestoft, Suffolk."
        path="/pricing"
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 hero-gradient relative overflow-hidden">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">
              Pricing
            </span>
            <h1
              data-testid="pricing-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6"
            >
              Simple, Transparent <span className="gradient-text">Pricing</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Choose the package that fits your business. All plans include hosting, domain, and ongoing support.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-500/10 border border-cyan-500/20">
              <span className="text-cyan-400 font-semibold text-sm tracking-wide">
                No Website Building Fees — Just a Simple Monthly Plan
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <PricingGrid tiers={pricingTiers} />
        </div>
      </section>

      {/* Add-ons */}
      <section className="py-20 lg:py-28 bg-gray-900/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">
              Optional Growth Services
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Enhance Your Package</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Add these services to any tier to accelerate your business growth.
            </p>
          </motion.div>
          <AddOnsSection groups={addOnGroups} />
        </div>
      </section>

      {/* Custom Quote */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-10 lg:p-16 rounded-2xl bg-gray-900/50 border border-gray-800"
          >
            <MessageCircle size={48} className="text-cyan-500 mx-auto mb-6" />
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">Let's Have a Chat</h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto text-lg">
              Not sure which package is right for you? Have specific requirements?
              We're happy to discuss your needs and find the perfect solution for your business.
              No pressure, just a friendly conversation about how we can help.
            </p>
            <Link to="/contact">
              <Button
                data-testid="pricing-contact-cta"
                size="lg"
                className="btn-primary text-white font-medium px-10 h-14 text-lg"
              >
                Get in Touch <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 lg:py-28 bg-gray-900/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">
              FAQ
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Common Questions</h2>
          </motion.div>
          <PricingFaq faqs={pricingFaqs} />
        </div>
      </section>
    </div>
  );
}
